import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import swal from 'sweetalert';
import { addWebhook, updateWebhook, deleteWebhookById } from '../../action/handbook';
import Input from '../utils/Input';

const TableWebhooks = ({ items }) => {
    const dispatch = useDispatch();
    const [editedItems, setEditedItems] = useState([]);
    
    useEffect(() => {
        const preparedItems = items.map(item => ({
            ...item,
            secret: item.secret || '',
            _originalSecret: item.secret || ''
        }));
        setEditedItems([{ url: '', name: '', isActive: true, secret: '', _originalSecret: '' }, ...preparedItems]);
    }, [items]);
    
    const handleChange = useCallback((index, field, value) => {
        setEditedItems(prevItems => {
            const updatedItems = [...prevItems];
            const currentItem = updatedItems[index];

            if (!currentItem) {
                return prevItems;
            }

            if (field === 'secret' && !currentItem.id) {
                return prevItems;
            }

            updatedItems[index] = {
                ...currentItem,
                [field]: value
            };

            return updatedItems;
        });
    }, []);

    const handleSecretBlur = useCallback((index) => {
        setEditedItems(prevItems => {
            const updatedItems = [...prevItems];
            const currentItem = updatedItems[index];

            if (!currentItem || !currentItem.id) {
                return prevItems;
            }

            if (!currentItem.secret || currentItem.secret.trim().length === 0) {
                updatedItems[index] = {
                    ...currentItem,
                    secret: currentItem._originalSecret || ''
                };
            }

            return updatedItems;
        });
    }, []);

    const handleSave = useCallback(async (item) => {
        if (!item.url && !item.id) return;

        if (item.id && (!item.secret || item.secret.trim().length === 0)) {
            await swal({
                title: "Ошибка",
                text: "Для существующего вебхука секрет обязателен.",
                icon: "error",
                button: "OK",
            });
            return;
        }

        try {
            if (item.id) {
                const payload = {
                    url: item.url,
                    name: item.name,
                    isActive: item.isActive
                };

                // Добавляем secret только если он есть
                if (item._originalSecret) {
                    payload.secret = item._originalSecret;
                }

                // Добавляем newSecret если секрет изменился
                if (item.secret && item.secret !== item._originalSecret) {
                    payload.newSecret = item.secret;
                }

                await dispatch(updateWebhook(item.id, payload));
            } else {
                const payload = {
                    url: item.url,
                    name: item.name,
                    isActive: item.isActive
                };
                await dispatch(addWebhook(payload));
            }
        } catch (error) {
            console.error('Ошибка при сохранении записи:', error);
        }
    }, [dispatch]);
    
    const handleDelete = useCallback(async (item) => {
        if (!item || !item.id) return;
        const secret = item._originalSecret;
        
        const willDelete = await swal({
            title: "Удаление вебхука",
            text: "Вы действительно хотите удалить вебхук?",
            icon: "warning",
            dangerMode: true,
            buttons: ["Отмена", "Удалить"],
        });
        
        if (willDelete) {
            try {
                await dispatch(deleteWebhookById(item.id, secret));
                setEditedItems(prev => prev.filter(current => current.id !== item.id));
            } catch (error) {
                console.error('Ошибка при удалении записи:', error);
            }
        }
    }, [dispatch]);

    const handleToggleActive = useCallback((index, item) => {
        const newIsActive = !item.isActive;
        handleChange(index, 'isActive', newIsActive);
        
        if (item.id) {
            handleSave({ ...item, isActive: newIsActive });
        }
    }, [handleChange, handleSave]);
    
    const columnStyles = useMemo(() => ({
        url: { verticalAlign: 'middle', textAlign: 'left', width: '30%' },
        name: { verticalAlign: 'middle', textAlign: 'left', width: '25%' },
        secret: { verticalAlign: 'middle', textAlign: 'left', width: '25%' },
        active: { verticalAlign: 'middle', textAlign: 'center', width: '10%' },
        actions: { verticalAlign: 'middle', textAlign: 'center', width: '10%' },
    }), []);

    const tableRows = useMemo(() => {
        return editedItems.map((item, index) => (
            <tr key={item.id || `new-${index}`} className="kt-datatable__row">
                <td className="kt-datatable__cell webhooks_url" style={columnStyles.url}>
                    <Input 
                        disableGroup
                        isControlledInput
                        value={item.url || ''} 
                        onChange={(e) => handleChange(index, 'url', e.target.value)} 
                        placeholder="https://example.com/webhook"
                    />
                </td>
                <td className="kt-datatable__cell webhooks_name" style={columnStyles.name}>
                    <Input 
                        disableGroup
                        isControlledInput
                        value={item.name || ''} 
                        onChange={(e) => handleChange(index, 'name', e.target.value)} 
                        placeholder="Название"
                    />
                </td>
                <td className="kt-datatable__cell webhooks_secret" style={columnStyles.secret}>
                    <Input 
                        disableGroup
                        isControlledInput
                        value={item.secret || ''}
                        onChange={(e) => handleChange(index, 'secret', e.target.value)}
                        onBlur={() => handleSecretBlur(index)}
                        required={Boolean(item.id)}
                        error={item.id && (!item.secret || item.secret.trim().length === 0) ? 'Секрет обязателен' : null}
                        placeholder={item.id ? 'Секрет' : 'Генерируется автоматически'}
                        disabled={!item.id}
                    />
                </td>
                <td className="kt-datatable__cell webhooks_active" style={columnStyles.active}>
                    <label className="kt-checkbox kt-checkbox--single kt-checkbox--all">
                        <input 
                            type="checkbox" 
                            checked={item.isActive || false}
                            onChange={() => handleToggleActive(index, item)}
                        />
                        <span></span>
                    </label>
                </td>
                <td className="kt-datatable__cell" style={columnStyles.actions}>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
                        <button
                            title="Сохранить изменения"
                            type="button"
                            onClick={() => handleSave(item)}
                            disabled={!item.url || (item.id && (!item.secret || item.secret.trim().length === 0))}
                            className="btn btn-outline-secondary btn-sm btn-icon"
                        >
                            <i className="flaticon2-check-mark"></i>                                                
                        </button>
                        <button
                            title="Удалить"
                            type="button"
                            onClick={() => handleDelete(item)}
                            disabled={!item.id}
                            className="btn btn-outline-secondary btn-sm btn-icon"
                        >
                            <i className="flaticon2-trash"></i>                                                
                        </button>
                    </div>
                </td>
            </tr>
        ));
    }, [columnStyles, editedItems, handleChange, handleSave, handleDelete, handleSecretBlur, handleToggleActive]);

    return (
        <table className="kt-datatable__table" style={{ minHeight: '500px', tableLayout: 'fixed', width: '100%' }}>
            <thead className="kt-datatable__head">
                <tr className="kt-datatable__row">
                    <th className="kt-datatable__cell webhooks_url" style={columnStyles.url}>URL</th>
                    <th className="kt-datatable__cell webhooks_name" style={columnStyles.name}>Название</th>
                    <th className="kt-datatable__cell webhooks_secret" style={columnStyles.secret}>Secret</th>
                    <th className="kt-datatable__cell webhooks_active" style={columnStyles.active}>Активен</th>
                    <th className="kt-datatable__cell" style={columnStyles.actions}>Действия</th>
                </tr>
            </thead>
            <tbody className="kt-datatable__body">
                {tableRows}
            </tbody>
        </table>
    );
};

export default React.memo(TableWebhooks);

